import { TELECONSULT_QUEUE_TIME_ZONE } from '../../utils/date';

function parseManagementDate(value?: string | null): Date | null {
  if (!value || !String(value).trim()) return null;
  const s = String(value).trim();
  const withOffset = /[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}+08:00`;
  const d = new Date(withOffset);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** VisitTimeStamp / DateVetted are clinic local (SGT), often serialized without Z. */
export function formatManagementDateTime(value?: string | null): string {
  const d = parseManagementDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-SG', {
    timeZone: TELECONSULT_QUEUE_TIME_ZONE,
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatManagementClock(value?: string | null): string {
  const d = parseManagementDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString('en-GB', {
    timeZone: TELECONSULT_QUEUE_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatManagementDay(value?: string | null): string {
  const d = parseManagementDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', {
    timeZone: TELECONSULT_QUEUE_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function secondsBetween(start?: string | null, end?: string | null): number | null {
  const a = parseManagementDate(start);
  const b = parseManagementDate(end);
  if (!a || !b || b < a) return null;
  return (b.getTime() - a.getTime()) / 1000;
}

/**
 * Stripe CompletedDate is stored as UTC; visit stamps are SGT with no offset.
 * Prefer the interpretation where payment is after register, using the shorter positive gap.
 */
export function secondsBetweenRegisterAndStripePay(
  registeredAt?: string | null,
  stripePaidAt?: string | null,
): number | null {
  const reg = parseManagementDate(registeredAt);
  if (!reg || !stripePaidAt || !String(stripePaidAt).trim()) return null;
  const raw = String(stripePaidAt).trim();
  const hasOffset = /[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw);
  const asClinic = parseManagementDate(raw);
  const asUtc = hasOffset ? asClinic : new Date(`${raw.replace(/[zZ]$/, '')}Z`);
  const candidates = [asClinic, asUtc && !Number.isNaN(asUtc.getTime()) ? asUtc : null];
  let best: number | null = null;
  candidates.forEach((paid) => {
    if (!paid || paid < reg) return;
    const sec = (paid.getTime() - reg.getTime()) / 1000;
    if (best == null || sec < best) best = sec;
  });
  return best;
}

export function formatDurationSeconds(seconds?: number | null, sampleCount?: number): string {
  if (seconds == null || !Number.isFinite(seconds) || (sampleCount != null && sampleCount <= 0)) {
    return '—';
  }
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export function formatDurationCompact(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${String(sec).padStart(2, '0')}s`;
}

export function formatSignedMinutes(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '';
  const m = Math.round(seconds / 60);
  if (m <= 0) return '+0m';
  return `+${m}m`;
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
